import { Box } from "@mui/material";
import { useMemo } from "react";
import {
  FormDataConsumer,
  RadioButtonGroupInput,
  ReferenceArrayInput,
  required,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useRecordContext,
  useTranslate,
} from "react-admin";
import { FieldValues } from "react-hook-form";

import { LOCATION_LIST_FETCH_LIMIT } from "../constants/pagination";
import { PractitionerStatus, Salutation, UpsertPractitionerInput } from "../generated/graphql";
import { WithAvatar } from "../hooks/useImageUploadHandler";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { space } from "../theme/designTokens";
import { PRACTITIONER_LOCATION_SCOPE, PractitionerLocationScope, PractitionerRecord } from "../types/practitioner";
import { AvatarInput } from "./AvatarInput";

export type PractitionerFormData = FieldValues &
  Pick<
    UpsertPractitionerInput,
    | "salutation"
    | "firstName"
    | "lastName"
    | "workerNumber"
    | "email"
    | "telephone"
    | "organizationId"
    | "status"
    | "role"
    | "motherTongue"
  > & {
    locationScope?: PractitionerLocationScope;
    locationIds?: string[];
  } & WithAvatar;

type PractitionerLocationSelection = Pick<PractitionerFormData, "locationScope" | "locationIds">;

export const serializePractitionerLocationScope = ({
  locationScope,
  locationIds,
}: PractitionerLocationSelection): string[] | undefined => {
  if (locationScope === PRACTITIONER_LOCATION_SCOPE.All) return [];
  if (locationScope !== PRACTITIONER_LOCATION_SCOPE.Selected || !locationIds?.length) return undefined;

  return Array.from(new Set(locationIds)).sort();
};

interface PractitionerFormProps {
  toolbar?: React.ReactElement;
}

export const PractitionerForm: React.FC<PractitionerFormProps> = ({ toolbar }) => {
  const record = useRecordContext<PractitionerRecord>();
  const translate = useTranslate();
  const { organizationId: selectedOrganizationId } = useOrganizationContext();

  const organizationId = record?.organizationId || selectedOrganizationId;

  // Transform locations array to locationIds for the form field.
  // Data provider returns locations as objects [{id, name, type}], but the form needs locationIds as string[]
  // because the mutation (UpsertPractitioner) expects locationIds for submission.
  // We use defaultValues instead of format prop because format(value) receives record.locationIds which doesn't exist,
  // so it would get undefined. defaultValues merges locationIds into the form record before fields read from it.
  const defaultValues = useMemo(
    () => ({
      locationScope: record?.locationScope,
      locationIds: record?.locations?.map((loc: { id: string; name: string; type: string | null }) => loc.id) || [],
      role: record?.role ?? "regular",
    }),
    [record?.locationScope, record?.locations, record?.role]
  );

  const salutationOptions = [
    { id: Salutation.Mister, name: translate("salutation.mister") },
    { id: Salutation.Missus, name: translate("salutation.missus") },
    { id: Salutation.Others, name: translate("salutation.others") },
  ];
  const roleOptions = [
    { id: "regular", name: translate("practitionerRoles.regular") },
    { id: "pdl", name: translate("practitionerRoles.pdl") },
    { id: "wbl", name: translate("practitionerRoles.wbl") },
    { id: "pflegefachkraft", name: translate("practitionerRoles.pflegefachkraft") },
    { id: "pflegehilfskraft", name: translate("practitionerRoles.pflegehilfskraft") },
    { id: "betreuungskraft", name: translate("practitionerRoles.betreuungskraft") },
    { id: "auszubildende", name: translate("practitionerRoles.auszubildende") },
    { id: "verwaltung", name: translate("practitionerRoles.verwaltung") },
    { id: "hauswirtschaft", name: translate("practitionerRoles.hauswirtschaft") },
    { id: "haustechnik", name: translate("practitionerRoles.haustechnik") },
    { id: "other", name: translate("practitionerRoles.other") },
    { id: "admin", name: translate("practitionerRoles.admin") },
  ];

  return (
    <SimpleForm toolbar={toolbar} defaultValues={defaultValues}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <AvatarInput name="avatar" source={record?.avatarUrl || undefined} />
        <SelectInput
          source="salutation"
          label={translate("salutationLabel")}
          choices={salutationOptions}
          fullWidth
          validate={required()}
        />
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: `${space.xs}px`, width: "100%" }}>
          <TextInput source="firstName" label={translate("firstName")} required fullWidth />
          <TextInput source="lastName" label={translate("lastName")} required fullWidth />
        </Box>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: `${space.xs}px`, width: "100%" }}>
          <TextInput source="workerNumber" label={translate("workerNumber")} fullWidth />
          <TextInput source="email" label={translate("email")} type="email" fullWidth />
        </Box>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: `${space.xs}px`, width: "100%" }}>
          <SelectInput
            source="role"
            label={translate("practitionerRole")}
            choices={roleOptions}
            translateChoice={false}
            fullWidth
            validate={required()}
          />
          <TextInput source="motherTongue" label={translate("motherTongue")} fullWidth />
        </Box>
        <TextInput source="telephone" label={translate("telephone")} fullWidth />
        <RadioButtonGroupInput
          source="locationScope"
          label={translate("practitionerLocationScope.label")}
          choices={[
            { id: PRACTITIONER_LOCATION_SCOPE.All, name: translate("practitionerLocationScope.all") },
            { id: PRACTITIONER_LOCATION_SCOPE.Selected, name: translate("practitionerLocationScope.selected") },
          ]}
          translateChoice={false}
          validate={required(translate("practitionerLocationScope.required"))}
        />
        <FormDataConsumer<PractitionerFormData>>
          {({ formData }) =>
            formData.locationScope === PRACTITIONER_LOCATION_SCOPE.Selected ? (
              <ReferenceArrayInput
                source="locationIds"
                reference="Location"
                filter={{ organizationId }}
                perPage={LOCATION_LIST_FETCH_LIMIT}
              >
                <SelectArrayInput
                  optionText="name"
                  fullWidth
                  label={translate("locations")}
                  size="medium"
                  validate={required(translate("practitionerLocationScope.selectedRequired"))}
                />
              </ReferenceArrayInput>
            ) : null
          }
        </FormDataConsumer>
        <SelectInput
          source="status"
          label={translate("status")}
          choices={[
            { id: PractitionerStatus.Active, name: translate("practitionerStatus.ACTIVE") },
            { id: PractitionerStatus.Inactive, name: translate("practitionerStatus.INACTIVE") },
          ]}
          fullWidth
          validate={required()}
        />
      </Box>
    </SimpleForm>
  );
};
